// TODO: Add some generic mechanism to handle errors and remove string comparison
import { isNonEmptyString } from './string';

const DEFAULT_MESSAGE_CODES = [500];

const UNHANDLED_ERROR_MESSAGES = [
  'Internal server error',
  'Unhandled organization pipe exception occurred',
  'Request failed with status code 403',
  'Request failed with status code 400',
  'Request failed with status code 500',
];

type SpreadsheetError = {
  invalidRows?: {
    errors?: {
      messages?: unknown[];
    }[];
  }[];
};

type FieldValidationMessage = {
  field?: string;
  messages?: unknown[];
};

const getSpreadsheetInvalidRowsMessage = (
  error: unknown
): string | undefined => {
  const { invalidRows } = (error as SpreadsheetError) ?? {};

  return invalidRows
    ?.flatMap(row => row.errors ?? [])
    .flatMap(fieldError => fieldError.messages ?? [])
    .find(isNonEmptyString);
};

const getValidationMessageFromArray = (
  message: unknown[]
): string | undefined => {
  const stringMessages = message.filter(isNonEmptyString);
  if (stringMessages.length > 0) return stringMessages.join(', ');

  const fieldMessages = message
    .flatMap(item => {
      if (!item || typeof item !== 'object' || !('messages' in item)) return [];

      return (item as FieldValidationMessage).messages ?? [];
    })
    .filter(isNonEmptyString);

  if (fieldMessages.length > 0) return fieldMessages.join(', ');

  return undefined;
};

const getResponseDataMessage = (data: unknown): string | undefined => {
  if (typeof data === 'string') return data;

  if (!data || typeof data !== 'object' || !('message' in data))
    return undefined;

  const { message } = data as { message: unknown };

  if (typeof message === 'string') return message;
  if (Array.isArray(message)) return getValidationMessageFromArray(message);

  return undefined;
};

export const extractErrorMessage = (
  error: unknown,
  defaultMessage: string = '',
  {
    defaultMessageStatusCodes = DEFAULT_MESSAGE_CODES,
  }: {
    defaultMessageStatusCodes?: number[];
  } = {}
) => {
  const errorMessage = (() => {
    const spreadsheetMessage = getSpreadsheetInvalidRowsMessage(error);

    if (spreadsheetMessage) return spreadsheetMessage;

    if (typeof error === 'string') return error;

    if (
      error &&
      typeof error === 'object' &&
      'status' in error &&
      typeof error.status === 'number'
    )
      if (defaultMessageStatusCodes.includes(error.status))
        return defaultMessage;

    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response
    ) {
      if (typeof error.response === 'string') return error.response;

      if (typeof error.response === 'object' && 'data' in error.response) {
        const responseMessage = getResponseDataMessage(error.response.data);
        if (responseMessage) return responseMessage;
      }
    }

    const directMessage = getResponseDataMessage(error);
    if (directMessage) return directMessage;

    if (error instanceof Error) return error.message;

    return defaultMessage;
  })();

  return UNHANDLED_ERROR_MESSAGES.includes(errorMessage)
    ? defaultMessage
    : errorMessage || defaultMessage;
};

const getErrorResponse = (error: unknown) =>
  error && typeof error === 'object' && 'response' in error
    ? (error.response as { data?: unknown } | undefined)
    : undefined;

const getFieldValidationMessages = (
  data: unknown
): FieldValidationMessage[] => {
  if (Array.isArray(data)) return data as FieldValidationMessage[];

  if (data && typeof data === 'object' && 'message' in data)
    return Array.isArray(data.message)
      ? (data.message as FieldValidationMessage[])
      : [];

  return [];
};

/** Joined validation messages the backend returned for one request field. */
export const getFieldValidationMessage = (
  error: unknown,
  field: string
): string | undefined => {
  const messages = getFieldValidationMessages(
    getErrorResponse(error)?.data
  ).reduce<string[]>((acc, fieldError) => {
    if (fieldError?.field !== field) return acc;

    for (const message of fieldError.messages ?? [])
      if (isNonEmptyString(message)) acc.push(message);

    return acc;
  }, []);

  return messages.length > 0 ? messages.join(', ') : undefined;
};
